/**
 * UploadContext
 * -------------
 * Runs the "new video" upload pipeline OUTSIDE of any modal so the user
 * can navigate around the app while it works. The Generate-Video modal
 * fires the upload and closes immediately; this provider owns:
 *
 *   - kicking off the GCS resumable chunked upload — or, for a pasted
 *     YouTube link, having the server download the video into GCS instead
 *     (everything after that step is identical for both sources)
 *   - generating the read-signed URL
 *   - posting the processing-options form to /videos/api/upload/
 *   - exposing live { percent, status, thumbnailUrl, video_id } so a
 *     persistent dock can render progress on every page
 *
 * Why not WebSockets for upload progress?
 *   The browser → GCS step is the slow bit, and the browser already
 *   knows exactly how many bytes it has shipped (chunkedUpload reports
 *   it). A WebSocket would only re-broadcast info we already have.
 *   Backend-side processing progress is delivered by the existing /ws
 *   channel and consumed by FilePage / FileWaitPage.
 */
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../api";
import { chunkedUpload } from "../utils/chunkedUpload";
import { importYouTubeVideo, YouTubeLinkError } from "../utils/youtubeImport";

const UploadContext = createContext(null);

export function useUpload() {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUpload must be used inside <UploadProvider>");
  }
  return ctx;
}

const INITIAL_STATE = {
  status: "idle", // idle | uploading | processing | success | error
  source: null, // "file" | "youtube"
  // YouTube only, while "uploading": queued | connecting | waiting |
  // downloading | finalizing (the server is downloading the video)
  stage: null,
  percent: 0,
  etaSeconds: null, // estimated seconds remaining
  speedBps: null, // YouTube download speed
  thumbnailUrl: null,
  video_id: null,
  error: null,
  fileName: null,
};

export function UploadProvider({ children }) {
  const [state, setState] = useState(INITIAL_STATE);
  const navigate = useNavigate();
  const inFlightRef = useRef(false);
  // Tracks which video the processing poller is currently following so a newer
  // upload supersedes an older one (the dock only shows one job at a time).
  const pollRef = useRef(null);

  const reset = useCallback(() => {
    pollRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  /**
   * Poll the backend for real processing progress + ETA. This is what makes
   * the "how much longer" indicator survive a page navigation/refresh: the
   * server persists progress on the Video document, so we just read it. Runs
   * detached; stops when this video is superseded or reaches a terminal state.
   */
  const startProcessingPoll = useCallback((video_id) => {
    pollRef.current = video_id;
    const startedAt = Date.now();
    const MAX_POLL_MS = 2 * 60 * 60 * 1000; // give up after 2h (backend cap)

    const poll = async () => {
      if (pollRef.current !== video_id) return; // superseded by a newer upload
      if (Date.now() - startedAt > MAX_POLL_MS) return; // stop polling forever

      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/videos/api/processing_status/${video_id}`,
          { withCredentials: true, timeout: 15000 }
        );

        if (pollRef.current !== video_id) return;

        if (data.status === "success") {
          setState((s) => ({ ...s, status: "success", percent: 100, etaSeconds: 0 }));
          return;
        }
        if (data.status === "error" || data.status === "not_found") {
          setState((s) => ({
            ...s,
            status: "error",
            etaSeconds: 0,
            error: "Processing failed. Please try again.",
          }));
          return;
        }

        setState((s) => ({
          ...s,
          status: "processing",
          // never let the bar go backwards
          percent: Math.max(s.percent || 0, data.progress || 0),
          etaSeconds:
            typeof data.eta_seconds === "number" ? data.eta_seconds : s.etaSeconds,
        }));
        setTimeout(poll, 5000);
      } catch (error) {
        if (pollRef.current !== video_id) return;
        if (error.response?.status === 404) {
          setState((s) => ({
            ...s,
            status: "error",
            etaSeconds: 0,
            error: "Processing failed. Please try again.",
          }));
          return;
        }
        // Transient error (network blip / auth refresh) — back off and retry.
        setTimeout(poll, 8000);
      }
    };

    poll();
  }, []);

  /**
   * Kick off the upload. Returns immediately (modal can close right after).
   * The actual work runs as a detached promise — errors land in state.error.
   */
  const startUpload = useCallback(
    (payload) => {
      if (inFlightRef.current) {
        message.warning(
          "An upload is already in progress. Please wait for it to finish."
        );
        return;
      }
      inFlightRef.current = true;

      const {
        currentUser,
        file, // File | null (null for YouTube)
        youtubeUrl, // string | null
        sourceTitle, // YouTube video title (shown in the progress dock)
        fileType, // mime or "text/youtube"
        thumbnail, // Blob
        thumbnailUrl, // object URL for preview
        audioLength,
        options, // censorSelected, muteCopyRight, censorImage, censorFaces, customCensorWords, censorMouth, generateClips, clipType, shortType (platform), language, generateCaptions, captionDesign, faceFocus, trackingMode, subjectPoint, nlEditingEnabled, nlPrompt, autopost*
        onPlanLimit, // optional callback (e.g. open payments modal)
      } = payload;

      const isYouTube = fileType === "text/youtube";

      setState({
        ...INITIAL_STATE,
        status: "uploading",
        source: isYouTube ? "youtube" : "file",
        stage: isYouTube ? "connecting" : null,
        thumbnailUrl,
        fileName: file?.name || sourceTitle || youtubeUrl || "video",
      });

      // Run pipeline detached from the caller.
      (async () => {
        try {
          // 1) reserve a video record + (for direct uploads) a GCS resumable URI
          const firstResponse = await axios.post(
            `${API_BASE_URL}/videos/api/create_video/`,
            {
              user_id: currentUser._id,
              audioLength,
              contentType: isYouTube ? "text/youtube" : file.type,
              ...(isYouTube && { youtube_url: youtubeUrl }),
            },
            { withCredentials: true, timeout: 60000 }
          );

          if (firstResponse.status !== 200) {
            const errMsg =
              firstResponse?.error ||
              "You have reached your plan limits. Please upgrade to continue.";
            message.info(errMsg);
            onPlanLimit?.();
            throw new Error(errMsg);
          }

          const video_id = firstResponse.data.video_id;
          setState((s) => ({ ...s, video_id }));

          // 2) get the video into GCS. A local file is uploaded straight from
          //    the browser in chunks; for a YouTube link our server downloads
          //    it instead (no upload from the user's connection at all).
          //    "uploading" shows the real byte progress either way.
          if (isYouTube) {
            await importYouTubeVideo(video_id, {
              onProgress: (p) => {
                setState((s) => ({
                  ...s,
                  stage: p.stage || s.stage,
                  percent:
                    typeof p.percent === "number"
                      ? Math.max(s.percent, Math.round(p.percent))
                      : s.percent,
                  etaSeconds:
                    typeof p.etaSeconds === "number" ? p.etaSeconds : null,
                  speedBps: p.speedBps || null,
                }));
              },
            });
          } else {
            await chunkedUpload(file, firstResponse.data.upload_uri, {
              onProgress: ({ percent }) => {
                setState((s) => ({ ...s, percent: Math.round(percent) }));
              },
            });
          }

          const get_signed_url = await axios.post(
            `${API_BASE_URL}/videos/api/generate_url/`,
            { video_id, generate: "read" },
            { withCredentials: true, timeout: 600000 }
          );
          const signed_url = get_signed_url.data.url;

          // 3) hand off to the backend — it starts processing and returns
          //    immediately. The "processing" phase percentage now reflects real
          //    server-side progress, polled below.
          setState((s) => ({
            ...s,
            status: "processing",
            stage: null,
            percent: 0,
            etaSeconds: null,
            speedBps: null,
          }));

          const formData = new FormData();
          formData.append("user_id", currentUser._id);
          formData.append("audioLength", audioLength);
          formData.append("thumbnail", thumbnail);
          formData.append("video_id", video_id);
          formData.append("muteCopyRight", options.muteCopyRight);
          formData.append("shortType", options.shortType);
          formData.append("censorSelected", options.censorSelected);
          formData.append("censorImage", options.censorImage);
          formData.append("generateClips", options.generateClips);
          formData.append("clipType", options.clipType);
          formData.append("language", options.language);
          formData.append("censorFaces", options.censorFaces);

          // Profanity add-ons — only meaningful when profanity censoring is on.
          // Custom words are sent as a JSON array; mouth-blur as a flag.
          if (options.censorSelected) {
            if (
              Array.isArray(options.customCensorWords) &&
              options.customCensorWords.length > 0
            ) {
              formData.append(
                "customCensorWords",
                JSON.stringify(options.customCensorWords)
              );
            }
            if (options.censorMouth) {
              formData.append("censorMouth", "true");
            }
          }

          // Platform TOS adherence: transcribe and mute/cut spoken segments
          // that risk a community-guidelines strike on the chosen platform.
          if (options.tosAdherence) {
            formData.append("tosAdherence", "true");
            formData.append("tosPlatform", options.tosPlatform || "youtube");
            formData.append(
              "tosMode",
              options.tosMode === "cut" ? "cut" : "mute"
            );
          }

          // Shorts-only creator options: TikTok-style captions and autonomous
          // face/speaker framing. Only meaningful for short clips, but always
          // forwarded — the backend ignores them for long clips.
          if (options.generateCaptions) {
            formData.append("generateCaptions", "true");
            formData.append("captionDesign", options.captionDesign || "hormozi");
          }
          if (options.faceFocus) {
            formData.append("faceFocus", "true");
            // Manual subject tracking: forward the user-marked subject point
            // (normalized 0..1) so the reframe locks onto that subject instead
            // of auto-detecting speakers.
            if (
              options.trackingMode === "manual" &&
              options.subjectPoint &&
              typeof options.subjectPoint.x === "number" &&
              typeof options.subjectPoint.y === "number"
            ) {
              formData.append("trackingMode", "manual");
              formData.append("subjectX", String(options.subjectPoint.x));
              formData.append("subjectY", String(options.subjectPoint.y));
            }
          }

          if (options.nlEditingEnabled && options.nlPrompt?.trim()) {
            formData.append("nlEditingEnabled", "true");
            formData.append("nlPrompt", options.nlPrompt.trim());
          }
          if (
            options.autopostEnabled &&
            options.generateClips &&
            options.autopostPlatforms?.length > 0
          ) {
            formData.append("autopostEnabled", "true");
            formData.append(
              "autopostPlatforms",
              options.autopostPlatforms.join(",")
            );
            formData.append("autopostCaptionStyle", options.captionStyle);

            // YouTube auto-post: forward the user-supplied title/description and
            // whether to post at the next optimal time (best activity window).
            if (options.autopostPlatforms.includes("youtube")) {
              if (options.youtubeTitle?.trim()) {
                formData.append("autopostYoutubeTitle", options.youtubeTitle.trim());
              }
              if (options.youtubeDescription?.trim()) {
                formData.append(
                  "autopostYoutubeDescription",
                  options.youtubeDescription.trim()
                );
              }
              formData.append(
                "autopostScheduleBestTime",
                options.scheduleBestTime === false ? "false" : "true"
              );
            }
          }
          formData.append("signed_url", signed_url);

          // This request returns as soon as the backend has accepted the job
          // ("Processing started"); the heavy work continues server-side.
          await axios.post(
            `${API_BASE_URL}/videos/api/upload/`,
            formData,
            {
              withCredentials: true,
              timeout: 1800000,
            }
          );

          if (options.autopostEnabled && options.autopostPlatforms?.length > 0) {
            const postsYouTube = options.autopostPlatforms.includes("youtube");
            message.success(
              postsYouTube && options.scheduleBestTime !== false
                ? "Upload done! Processing now — your YouTube post will go out at the next best time."
                : "Upload done! Processing now — it will auto-post when ready."
            );
          } else {
            message.success(
              isYouTube
                ? "Video downloaded from YouTube — processing in the background. You can keep working."
                : "Upload done — processing in the background. You can keep working."
            );
          }

          // Hand-off succeeded; release the upload lock so the user can queue
          // another video, and follow real processing progress from here.
          inFlightRef.current = false;
          startProcessingPoll(video_id);
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              error_type: isYouTube ? "youtube_import" : "background_upload",
              youtube_error_code: error.code,
            },
          });

          const status = error.response?.status;
          const backendErrorMessage = error.response?.data?.error;
          let uiMessage = "Upload failed. Please try again.";

          if (error instanceof YouTubeLinkError) {
            // Already a user-facing explanation from the server (private,
            // too long for the plan, YouTube blocking downloads, …).
            uiMessage = error.message;
            if (error.code === "TOO_LONG") onPlanLimit?.();
            if (error.code === "AUTH") navigate("/login?operation=uploadFile");
          } else {
            switch (status) {
              case 401:
                uiMessage = "Please log in to upload a video";
                navigate("/login?operation=uploadFile");
                break;
              case 400:
                uiMessage = isYouTube
                  ? backendErrorMessage || "That YouTube link isn't valid."
                  : "Please select a valid file to upload";
                break;
              case 403:
                // The upload routes also reject options the plan isn't entitled
                // to (e.g. natural language editing on Free) — send those to the
                // upgrade prompt rather than the generic authorization message.
                if (error.response?.data?.code === "PLAN_UPGRADE_REQUIRED") {
                  uiMessage =
                    backendErrorMessage ||
                    "That option requires a paid plan — upgrade to continue.";
                  onPlanLimit?.();
                } else {
                  uiMessage = "You are not authorized to upload a video";
                }
                break;
              case 404:
                uiMessage = backendErrorMessage || "User data not found.";
                onPlanLimit?.();
                break;
              case 405:
                uiMessage =
                  backendErrorMessage ||
                  "Free plan limit reached — upgrade to continue.";
                onPlanLimit?.();
                break;
              case 409:
                uiMessage = backendErrorMessage || "Please verify your email.";
                navigate("/send-email-verification");
                break;
              case 500:
                uiMessage = "Something went wrong on our side. Please retry.";
                break;
              default:
                uiMessage = error.message || uiMessage;
            }
          }

          message.error(uiMessage);
          setState((s) => ({
            ...s,
            status: "error",
            error: uiMessage,
          }));
        } finally {
          inFlightRef.current = false;
        }
      })();
    },
    [navigate, startProcessingPoll]
  );

  const dismiss = useCallback(() => {
    if (inFlightRef.current) return; // can't dismiss while the upload is running
    pollRef.current = null; // stop following processing progress
    setState(INITIAL_STATE);
  }, []);

  return (
    <UploadContext.Provider
      value={{ ...state, startUpload, dismiss, reset }}
    >
      {children}
    </UploadContext.Provider>
  );
}
